import React, { useState } from 'react';
import { HiOutlineTrash } from 'react-icons/hi';
import TransactionDetailModal from './TransactionDetailModal';
import { formatApiDateTime } from '../../../lib/helpers';

function TransactionsBox({ transactions, onReverse }) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };
  const validTransactions = transactions ? transactions.filter(t => t.status !== 'reversed') : [];

  if (!validTransactions || validTransactions.length === 0) {
    return (
      <div className="bg-white p-8 rounded-lg shadow border border-gray-200 text-center text-gray-500">
        No hay transacciones para mostrar
      </div>
    );
  }

  const formatDate = (dateString) =>
    formatApiDateTime(dateString, { fallback: '-' });

  const getTypeColor = (type) => {
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'bg-green-100 text-green-800';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'bg-purple-100 text-purple-800';
    if (t === 'refund' || t === 'reembolso') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'Venta';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'Crédito';
    if (t === 'refund' || t === 'reembolso') return 'Reembolso';
    return type || '-';
  };

  const isVenta = (type) => {
    const t = type?.toLowerCase();
    return t === 'purchase' || t === 'sale' || t === 'venta';
  };

  const handleReverseClick = (e, transaction) => {
    e.stopPropagation();
    if (onReverse) onReverse(transaction);
  };

  return (
    <>
      {/* Modal de detalles */}
      <TransactionDetailModal
        active={isModalOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
        onReverse={onReverse}
      />

      {/* Vista de tarjetas para móvil */}
      <div className="block md:hidden space-y-3">
        {validTransactions.map((transaction) => (
          <div
            key={transaction.id}
            className="bg-white rounded-lg shadow border border-gray-200 p-3 hover:shadow-md transition-shadow"
          >
            <div
              className="flex justify-between items-start mb-2 cursor-pointer"
              onClick={() => handleTransactionClick(transaction)}
            >
              <div className="flex-1">
                <div className="text-xs text-gray-500">ID: #{transaction.id?.toString().slice(0, 8) || '-'}</div>
                <div className="font-medium text-sm mt-1">
                  {transaction.user_name || transaction.user_email || `Usuario #${transaction.user_id?.toString().slice(0, 8)}` || '-'}
                </div>
                {transaction.user_name && transaction.user_email && (
                  <div className="text-xs text-gray-500">{transaction.user_email}</div>
                )}
              </div>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                {getTypeLabel(transaction.type)}
              </span>
            </div>

            <div
              className="grid grid-cols-2 gap-2 text-sm mt-3 cursor-pointer"
              onClick={() => handleTransactionClick(transaction)}
            >
              <div>
                <span className="text-gray-500 text-xs">Monto:</span>
                <div className="font-semibold">€{parseFloat(transaction.amount || 0).toFixed(2)}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Fecha:</span>
                <div className="text-xs">{formatDate(transaction.timestamp || transaction.created_at)}</div>
              </div>
            </div>

            {transaction.items && transaction.items.length > 0 && (
              <details className="mt-3 cursor-pointer">
                <summary
                  className="text-blue-600 hover:text-blue-800 text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  {transaction.items.length} producto{transaction.items.length !== 1 ? 's' : ''}
                </summary>
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                  {transaction.items.map((item, index) => (
                    <div key={index} className="border-b border-gray-200 pb-1 last:border-b-0">
                      <div className="font-medium">{item.product_name || `Producto #${item.product_id}`}</div>
                      <div className="text-gray-600">
                        Cantidad: {item.quantity} × €{parseFloat(item.unit_price || 0).toFixed(2)} =
                        €{(item.quantity * parseFloat(item.unit_price || 0)).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {isVenta(transaction.type) && onReverse && (
              <button
                type="button"
                onClick={(e) => handleReverseClick(e, transaction)}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700"
              >
                <HiOutlineTrash className="w-4 h-4" /> Anular venta
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Vista de tabla para desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-gray-700">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th scope="col" className="py-3 px-4">ID</th>
                <th scope="col" className="py-3 px-4">Usuario</th>
                <th scope="col" className="py-3 px-4">Tipo</th>
                <th scope="col" className="py-3 px-4">Monto</th>
                <th scope="col" className="py-3 px-4">Fecha</th>
                <th scope="col" className="py-3 px-4">Productos</th>
                <th scope="col" className="py-3 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {validTransactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                >
                  <td
                    className="py-3 px-4 font-mono text-sm cursor-pointer"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    #{transaction.id?.toString().slice(0, 8) || '-'}
                  </td>
                  <td
                    className="py-3 px-4 cursor-pointer"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    <div className="text-sm font-medium">
                      {transaction.user_name || transaction.user_email || `Usuario #${transaction.user_id?.toString().slice(0, 8)}` || '-'}
                    </div>
                    {transaction.user_name && transaction.user_email && (
                      <div className="text-xs text-gray-500">{transaction.user_email}</div>
                    )}
                  </td>
                  <td
                    className="py-3 px-4 cursor-pointer"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(transaction.type)}`}>
                      {getTypeLabel(transaction.type)}
                    </span>
                  </td>
                  <td
                    className="py-3 px-4 font-semibold cursor-pointer"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    €{parseFloat(transaction.amount || 0).toFixed(2)}
                  </td>
                  <td
                    className="py-3 px-4 text-sm cursor-pointer"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    {formatDate(transaction.timestamp || transaction.created_at)}
                  </td>
                  <td className="py-3 px-4">
                    {transaction.items && transaction.items.length > 0 ? (
                      <details className="cursor-pointer">
                        <summary className="text-blue-600 hover:text-blue-800 text-sm">
                          {transaction.items.length} producto{transaction.items.length !== 1 ? 's' : ''}
                        </summary>
                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                          {transaction.items.map((item, index) => (
                            <div key={index} className="border-b border-gray-200 pb-1 last:border-b-0">
                              <div className="font-medium">{item.product_name || `Producto #${item.product_id}`}</div>
                              <div className="text-gray-600">
                                Cantidad: {item.quantity} × €{parseFloat(item.unit_price || 0).toFixed(2)} =
                                €{(item.quantity * parseFloat(item.unit_price || 0)).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <span className="text-gray-400 text-sm">Sin items</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {isVenta(transaction.type) && onReverse && (
                      <button
                        type="button"
                        title="Anular venta (devolver stock y saldo)"
                        onClick={(e) => handleReverseClick(e, transaction)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default TransactionsBox;
